import { useMemo } from 'react'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import { useRbacRoleOptions } from './useRbacRoleOptions'

interface FundingInviteAccess {
  /**
   * True when this admin may grant and revoke funding access, and we know which
   * role to grant. Callers must treat it as an all-or-nothing gate: without it,
   * a flow that also invites has to skip the invite half rather than try it.
   */
  allowed: boolean
  /** The invite-allowed role ids to grant, empty until they resolve. */
  roleIds: string[]
}

/**
 * The one gate for a flow that grants or revokes funding access as a side
 * effect of something else, such as the HubSpot sync. It has to clear the same
 * bar the User View tab does, plus have a role to act on.
 */
export const useFundingInviteAccess = (): FundingInviteAccess => {
  const { flags } = useFeatureFlags()
  const { allowed: canManageOnboardingUser } = usePermission(
    RbacActions.Manage,
    RbacSubjects.OnboardingUser
  )
  const { options } = useRbacRoleOptions()

  const roleIds = useMemo(
    () => options.map((option) => option.value),
    [options]
  )

  return {
    allowed:
      Boolean(flags['admin.finance.user-invitations-enabled']) &&
      canManageOnboardingUser &&
      roleIds.length > 0,
    roleIds,
  }
}
